// ⚠️ If you modify the styling of this component and you're using the SectionListings component in your marketplace (featured listings)
// please reflect those changes in the calculateCarouselHeight function in SectionListing.js to avoid layout issues
import React from 'react';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';

import { useIntl } from '../../util/reactIntl';
import { requireListingImage } from '../../util/configHelpers';
import { lazyLoadWithDimensions } from '../../util/uiHelpers';
import { createSlug } from '../../util/urlHelpers';

import {
  AspectRatioWrapper,
  NamedLink,
  ResponsiveImage,
  ListingCardThumbnail,
  IconLocation,
  IconReviewStar,
} from '../../components';

import { getListingCardTranslations } from './ListingCard.helpers';

import css from './ListingCard.module.css';

// Figma listing card image: 305 × 224
export const LISTING_CARD_IMAGE_WIDTH = 305;
export const LISTING_CARD_IMAGE_HEIGHT = 224;

const LazyImage = lazyLoadWithDimensions(ResponsiveImage, { loadAfterInitialRendering: 3000 });

const IconHeart = () => (
  <svg
    className={css.favoriteIcon}
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M12 20.25s-7.5-4.686-7.5-10.125A4.125 4.125 0 0 1 12 6.938a4.125 4.125 0 0 1 7.5 3.187C19.5 15.564 12 20.25 12 20.25Z"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const IconGuests = () => (
  <svg
    className={css.guestsIcon}
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M16 19v-1.2c0-1.68 0-2.52-.327-3.162a3 3 0 0 0-1.311-1.311C13.72 13 12.88 13 11.2 13H8.8c-1.68 0-2.52 0-3.162.327a3 3 0 0 0-1.311 1.311C4 15.28 4 16.12 4 17.8V19"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M13.5 7.5a3.5 3.5 0 1 1-7 0 3.5 3.5 0 0 1 7 0Z"
      stroke="currentColor"
      strokeWidth="1.8"
    />
    <path
      d="M20 19v-1.05c0-1.47 0-2.205-.286-2.766a2.625 2.625 0 0 0-1.148-1.148C18.005 13.75 17.27 13.75 15.8 13.75"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M16.5 8.125a2.875 2.875 0 0 0 0-4.25"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
  </svg>
);

/**
 * ListingCardImage
 * Component responsible for rendering the image part of the listing card.
 * It either renders the first image from the listing's images array with lazy loading,
 * or a stylized placeholder if images are disabled for the listing type.
 * Also wraps the image in a fixed aspect ratio container for consistent layout.
 * @component
 * @param {Object} props
 * @param {Object} props.listing listing entity with image data
 * @param {Function?} props.setActivePropsMaybe mouse enter/leave handlers for map highlighting
 * @param {string} props.title listing title for alt text
 * @param {string} props.renderSizes img/srcset size rules
 * @param {number} props.aspectWidth aspect ratio width
 * @param {number} props.aspectHeight aspect ratio height
 * @param {string} props.variantPrefix image variant prefix (e.g. "listing-card")
 * @param {boolean} props.showListingImage whether to show actual listing image or not
 * @param {Object?} props.style the background color for the listing card with no image
 * @returns {JSX.Element} listing image with fixed aspect ratio or fallback preview
 */
const ListingCardImage = props => {
  const {
    listing,
    setActivePropsMaybe,
    title,
    renderSizes,
    aspectWidth,
    aspectHeight,
    variantPrefix,
    aspectRatioClassName,
    lazyLoadImage,
  } = props;

  const firstImage = listing?.images?.[0] || null;
  const variants = firstImage
    ? Object.keys(firstImage?.attributes?.variants).filter(k => k.startsWith(variantPrefix))
    : [];

  const aspectRatioClass = aspectRatioClassName || css.aspectRatioWrapper;
  const ImageComponent = lazyLoadImage ? LazyImage : ResponsiveImage;

  return (
    <AspectRatioWrapper
      className={aspectRatioClass}
      width={aspectWidth}
      height={aspectHeight}
      {...setActivePropsMaybe}
    >
      <ImageComponent
        rootClassName={css.rootForImage}
        alt={title}
        image={firstImage}
        variants={variants}
        sizes={renderSizes}
      />
    </AspectRatioWrapper>
  );
};

/**
 * ListingCard
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to component's own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {string?} props.aspectRatioClassName custom className for AspectRatioWrapper component
 * @param {Object} props.listing API entity: listing or ownListing
 * @param {string?} props.renderSizes for img/srcset
 * @param {Function?} props.setActiveListing
 * @param {boolean?} props.showAuthorInfo
 * @returns {JSX.Element} listing card to be used in search result panel etc.
 */
export const ListingCard = props => {
  const config = useConfiguration();
  const intl = props.intl || useIntl();

  const {
    className,
    rootClassName,
    aspectRatioClassName,
    darkMode,
    listing,
    renderSizes,
    setActiveListing,
    showAuthorInfo = false,
    lazyLoadImage = true,
  } = props;

  const translations = getListingCardTranslations(listing, config, intl);
  const {
    titlePlain,
    titleFormatted,
    cardAriaLabel,
    showPrice,
    priceTooltip,
    priceMessage,
    authorName,
    locationLabel,
    formattedRating,
    guestsAndReviewsMessage,
    favoriteLabel,
  } = translations;

  const classes = classNames(rootClassName || css.root, className);

  const id = listing?.id?.uuid;
  const { title = '', publicData } = listing?.attributes || {};
  const slug = createSlug(title);

  const { listingType, cardStyle } = publicData || {};
  const validListingTypes = config.listing.listingTypes || [];
  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  // Render the listing image only if listing images are enabled in the listing type
  const showListingImage = requireListingImage(foundListingTypeConfig);

  const { variantPrefix = 'listing-card' } = config.layout.listingImage;
  const aspectWidth = LISTING_CARD_IMAGE_WIDTH;
  const aspectHeight = LISTING_CARD_IMAGE_HEIGHT;

  // Sets the listing as active in the search map when hovered (if the search map is enabled)
  const setActivePropsMaybe = setActiveListing
    ? {
        onMouseEnter: () => setActiveListing(listing?.id),
        onMouseLeave: () => setActiveListing(null),
      }
    : null;

  const handleFavoriteClick = event => {
    event.preventDefault();
    event.stopPropagation();
  };

  return (
    <div className={classes}>
      <NamedLink
        className={css.content}
        name="ListingPage"
        params={{ id, slug }}
        ariaLabel={cardAriaLabel}
      >
        <div className={css.imageWrap}>
          {showListingImage ? (
            <ListingCardImage
              renderSizes={renderSizes}
              title={titlePlain}
              listing={listing}
              setActivePropsMaybe={setActivePropsMaybe}
              aspectWidth={aspectWidth}
              aspectHeight={aspectHeight}
              variantPrefix={variantPrefix}
              aspectRatioClassName={aspectRatioClassName}
              lazyLoadImage={lazyLoadImage}
            />
          ) : (
            <ListingCardThumbnail
              style={cardStyle}
              listingTitle={title}
              className={aspectRatioClassName}
              width={aspectWidth}
              height={aspectHeight}
              setActivePropsMaybe={setActivePropsMaybe}
            />
          )}
        </div>
        <div className={css.info}>
          {showListingImage && (
            <div className={classNames(css.title, { [css.lightText]: darkMode })}>
              {titleFormatted}
            </div>
          )}
          {locationLabel || formattedRating ? (
            <div className={css.metaRow}>
              {locationLabel ? (
                <span className={css.location}>
                  <IconLocation className={css.locationIcon} />
                  <span>{locationLabel}</span>
                </span>
              ) : null}
              {formattedRating ? (
                <span className={css.rating}>
                  <IconReviewStar isFilled className={css.ratingStar} />
                  <span>{formattedRating}</span>
                </span>
              ) : null}
            </div>
          ) : null}
          {guestsAndReviewsMessage ? (
            <div className={css.capacityRow}>
              <IconGuests />
              <span>{guestsAndReviewsMessage}</span>
            </div>
          ) : null}
          {showAuthorInfo ? (
            <div className={classNames(css.authorInfo, { [css.lightText]: darkMode })}>
              {authorName}
            </div>
          ) : null}
          {showPrice ? (
            <div className={css.price} title={priceTooltip}>
              {priceMessage}
            </div>
          ) : null}
        </div>
      </NamedLink>
      <button
        type="button"
        className={css.favoriteButton}
        aria-label={favoriteLabel}
        onClick={handleFavoriteClick}
      >
        <IconHeart />
      </button>
    </div>
  );
};

export default ListingCard;
